import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserMenu } from './UserMenu';
import { Button } from './design-system';

interface HeaderProps {
  /** Title to display in the header */
  title?: string;
  /** Show the Add Recipe button */
  showAddRecipe?: boolean;
}

interface NavLinkProps {
  to: string;
  children: React.ReactNode;
  className: string;
  isActive: boolean;
}

interface MobileMenuProps {
  isOpen: boolean;
  onToggle: () => void;
}

const NavLink: React.FC<NavLinkProps> = ({ to, children, className, isActive }) => (
  <Link to={to} className={className} aria-current={isActive ? 'page' : undefined}>
    {children}
  </Link>
);

const MobileMenuButton: React.FC<MobileMenuProps> = ({ isOpen, onToggle }) => (
  <Button
    onClick={onToggle}
    variant="ghost"
    size="md"
    className="lg:hidden"
    aria-label="Toggle navigation menu"
    aria-expanded={isOpen}
  >
    {isOpen ? (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ) : (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    )}
  </Button>
);

const AddRecipeButton: React.FC = () => {
  const PlusIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
  
  return (
    <Link to="/app/recipes/add">
      <button
        className="inline-flex items-center justify-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold text-white !min-h-[32px] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#E07A55] focus:ring-offset-2"
        style={{
          background: 'linear-gradient(135deg, #E07A55 0%, #D06845 100%)',
          boxShadow: '0 4px 8px rgba(224, 122, 85, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.3)',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, #E58765 0%, #E07A55 100%)';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 6px 12px rgba(224, 122, 85, 0.4), 0 3px 6px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, #E07A55 0%, #D06845 100%)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(224, 122, 85, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.3)';
        }}
        onMouseDown={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, #C05835 0%, #B04825 100%)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(224, 122, 85, 0.2), 0 1px 2px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(0, 0, 0, 0.1)';
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, #E58765 0%, #E07A55 100%)';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 6px 12px rgba(224, 122, 85, 0.4), 0 3px 6px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.3)';
        }}
      >
        <PlusIcon />
        Add Recipe
      </button>
    </Link>
  );
};

/**
 * Reusable Header Component - Optimized for code organization
 */
export const Header: React.FC<HeaderProps> = ({ title = 'DinnerBrain', showAddRecipe = true }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };
    
    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMobileMenuOpen]);
  
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  
  const isActiveLink = (path: string) => {
    if (path === '/app/recipes') {
      return location.pathname === '/app/recipes' || location.pathname === '/app' || location.pathname === '/app/';
    }
    return location.pathname.startsWith(path);
  };
  
  const getNavLinkClass = (path: string) => {
    const base = 'text-base font-geist transition-colors relative';
    const active = 'font-bold text-primary after:content-[\'\'] after:absolute after:bottom-[-4px] after:left-0 after:right-0 after:h-[2px] after:bg-brand-primary after:rounded-full';
    const inactive = 'font-medium text-secondary hover:text-primary';
    return `${base} ${isActiveLink(path) ? active : inactive}`;
  };
  
  const getMobileNavLinkClass = (path: string) => {
    const base = 'block px-4 py-3 text-base font-geist transition-colors rounded-lg';
    const active = 'font-bold text-primary bg-brand-primary/10 border-l-4 border-brand-primary';
    const inactive = 'font-medium text-secondary hover:text-primary hover:bg-surface-secondary';
    return `${base} ${isActiveLink(path) ? active : inactive}`;
  };

  const navItems = [
    { path: '/app/recipes', label: 'Recipes' },
    { path: '/app/meal-plan', label: 'Meal Plan' },
    { path: '/app/shopping-list', label: 'Shopping List' },
    { path: '/app/community', label: 'Community' }
  ];
  
  return (
    <header className="hidden md:block sticky top-0 z-50 backdrop-blur-md bg-transparent">
      <div className="relative mx-auto px-4 sm:px-6 lg:px-8 py-1" style={{ backgroundColor: '#E6EFEA' }}>
        <div className="flex justify-between items-center bg-white/90 backdrop-blur-sm rounded-xl px-4 py-0.5 shadow-md">
          <div className="flex items-center gap-4">
            <MobileMenuButton isOpen={isMobileMenuOpen} onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />
            <Link to="/app/recipes" className="flex items-center gap-2 text-xl font-bold text-primary hover:text-secondary transition-colors">
              <span className="font-jakarta font-semibold tracking-tight">{title}</span>
            </Link>
          </div>
          
          <nav id="main-navigation" role="navigation" aria-label="Main navigation" className="hidden lg:flex items-center gap-6">
            {navItems.map(item => (
              <NavLink key={item.path} to={item.path} className={getNavLinkClass(item.path)} isActive={isActiveLink(item.path)}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {showAddRecipe && <AddRecipeButton />}
            <Link 
              to="/knowledge-base" 
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 flex items-center justify-center"
              aria-label="Help and Documentation"
              title="Help & Documentation"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </Link>
            <UserMenu />
          </div>
        </div>
        
        {isMobileMenuOpen && (
          <div ref={menuRef} className="lg:hidden absolute top-full left-0 right-0 mt-2 mx-6">
            <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg py-2 px-2">
              {navItems.map(item => (
                <NavLink key={item.path} to={item.path} className={getMobileNavLinkClass(item.path)} isActive={isActiveLink(item.path)}>
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};